import { cloneDeepWith } from 'lodash-es';
import {
  seoSegmentationListingPage,
  seoSegmentationListingPage_getSEOSegmentationLinkSections as SEOSegmentationLinkSections,
} from '@/__generated__/seoSegmentationListingPage';
import { usePageAttributes } from '@/components/contexts/PageAttributesContext';
import { InternalLinkSectionId, SEOSegmentationPageType } from '@/__generated__/globalTypes';

export const useInternalLinking = (
  internalLinkingSections: seoSegmentationListingPage['getSEOSegmentationLinkSections']
): seoSegmentationListingPage['getSEOSegmentationLinkSections'] => {
  const { pageType, segmentNameSingular, segmentNamePlural } = usePageAttributes();

  const getSectionTitle = (sectionId: string | null) => {
    if (sectionId === InternalLinkSectionId.RELATED_SERVICES) {
      return 'Sub-categories';
    }
    if (sectionId === InternalLinkSectionId.TOP_CITIES) {
      if (pageType === SEOSegmentationPageType.NATIONAL) {
        return `${segmentNameSingular} in Top Cities`;
      }
      return `${segmentNamePlural} in Nearby Cities`;
    }
    if (sectionId === InternalLinkSectionId.RESOURCES) {
      return 'More Services';
    }
    return null;
  };

  const filterSections = (
    seoLinkSections: SEOSegmentationLinkSections[]
  ): SEOSegmentationLinkSections[] => {
    return seoLinkSections.reduce<SEOSegmentationLinkSections[]>((result, section) => {
      // Filter out ARTICLES and JOBS section
      if (
        section.id === InternalLinkSectionId.ARTICLES ||
        section.id === InternalLinkSectionId.JOBS
      ) {
        return result;
      }

      // Find RESOURCES section
      if (section.id === InternalLinkSectionId.RESOURCES) {
        const jobSection = seoLinkSections.find(
          (currentSection) => currentSection.id === InternalLinkSectionId.JOBS
        );

        // If JOBS section exists, combine its linkList with RESOURCES section's linkList
        if (jobSection) {
          const combinedSection: SEOSegmentationLinkSections = {
            ...section,
            linkList: section.linkList.concat(jobSection.linkList),
          };

          return [...result, combinedSection];
        }
      }

      return [...result, section];
    }, []);
  };

  const sortSections = (
    seoLinkSections: SEOSegmentationLinkSections[],
    nationalOrLocalPageType: string | undefined = undefined
  ): SEOSegmentationLinkSections[] => {
    const nationalPageTabOrder: { [key: string]: number } = Object.freeze({
      RELATED_SERVICES: 1,
      TOP_CITIES: 2,
      RESOURCES: 3,
      NULL_ID: 4,
    });
    const localPageTabOrder: { [key: string]: number } = Object.freeze({
      TOP_CITIES: 1,
      RELATED_SERVICES: 2,
      RESOURCES: 3,
      NULL_ID: 4,
    });

    return cloneDeepWith(seoLinkSections).sort(
      (linkSectionA: SEOSegmentationLinkSections, linkSectionB: SEOSegmentationLinkSections) => {
        const sectionAId = linkSectionA.id ? linkSectionA.id.toString() : 'NULL_ID';
        const sectionBId = linkSectionB.id ? linkSectionB.id.toString() : 'NULL_ID';
        if (nationalOrLocalPageType === SEOSegmentationPageType.NATIONAL) {
          return nationalPageTabOrder[sectionAId] - nationalPageTabOrder[sectionBId];
        }
        return localPageTabOrder[sectionAId] - localPageTabOrder[sectionBId];
      }
    );
  };

  if (!internalLinkingSections) return null;

  const filteredSections = filterSections(internalLinkingSections);
  return sortSections(filteredSections, pageType).map((section) => ({
    ...section,
    title: getSectionTitle(section.id) ?? section.title,
  }));
};
